import { Link } from "react-router-dom";
import { heroStats, hotTags, hotTopics, siteMeta } from "@/mocks/community";

function rankStyle(rank: number) {
  if (rank === 1) return "bg-primary-500 text-background-50";
  if (rank === 2) return "bg-primary-400 text-background-50";
  if (rank === 3) return "bg-accent-500 text-primary-950";
  return "bg-background-200 text-foreground-700";
}

export default function Hero() {
  const top = hotTopics.slice(0, 5);

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20editorial%20abstract%20background%20with%20flowing%20crimson%20and%20amber%20gradient%20shapes%20over%20cream%20paper%20texture%2C%20soft%20directional%20studio%20lighting%2C%20elegant%20modern%20poster%20composition%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=baoliao-hero-bg&orientation=landscape"
          alt="爆料社区首屏抽象暖色背景"
          title="爆料社区 - 今日活跃话题榜"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/70 to-primary-950/55" />
      </div>

      <div className="relative mx-auto flex max-w-container flex-col gap-10 px-4 pb-12 pt-28 md:px-6 md:pb-16 md:pt-32 lg:flex-row lg:items-center lg:gap-12">
        <div className="w-full lg:w-[1.1fr]">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-100 backdrop-blur">
            <span className="h-2 w-2 animate-pulse rounded-full bg-accent-400" aria-hidden="true" />
            今日活跃话题榜 · 实时更新中
          </span>
          <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            爆料社区，第一时间围观
            <span className="text-accent-400">今日活跃话题</span>
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            高互动爆料帖、板块热榜与新帖热门讨论集中在这里。打开爆料社区，第一时间掌握今日活跃话题与高互动爆料帖，
            新帖热门讨论随时刷新，满足你的围观好奇心。
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/topics"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-fire-line" aria-hidden="true" />
              查看今日话题榜
            </Link>
            <Link
              to="/about#submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-send-plane-line" aria-hidden="true" />
              我要爆料投稿
            </Link>
          </div>

          <dl className="mt-9 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {heroStats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-100/15 bg-background-50/10 p-3 backdrop-blur">
                <dt className="flex items-center gap-1.5 text-xs text-background-200/90">
                  <i className={stat.icon} aria-hidden="true" />
                  {stat.label}
                </dt>
                <dd className="mt-1.5 font-heading text-lg font-bold text-background-50 md:text-xl">{stat.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="w-full lg:w-[0.9fr]">
          <div className="rounded-2xl border border-background-100/20 bg-background-50/95 p-5 backdrop-blur md:p-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                <i className="ri-fire-fill text-primary-500" aria-hidden="true" />
                今日活跃话题榜
              </h2>
              <Link to="/topics" className="text-xs font-medium text-primary-600 transition-colors hover:text-primary-700">
                完整榜单
              </Link>
            </div>
            <ol className="mt-4 space-y-1">
              {top.map((topic) => (
                <li key={topic.rank}>
                  <Link
                    to={`/post/${topic.postId}`}
                    className="group flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-primary-50"
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold ${rankStyle(
                        topic.rank,
                      )}`}
                    >
                      {topic.rank}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                        {topic.title}
                      </span>
                      <span className="mt-0.5 flex items-center gap-3 text-xs text-foreground-500">
                        <span>{topic.categoryName}</span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                          {(topic.heat / 10000).toFixed(1)}万
                        </span>
                      </span>
                    </span>
                    {topic.trend === "new" && (
                      <span className="shrink-0 rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-900">
                        新
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div className="relative border-t border-background-100/15 bg-foreground-950/40 backdrop-blur">
        <div className="mx-auto flex max-w-container items-center gap-4 px-4 py-3 md:px-6">
          <span className="hidden shrink-0 items-center gap-1.5 text-xs font-medium text-accent-300 sm:flex">
            <i className="ri-flashlight-line" aria-hidden="true" />
            热门标签
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="daily-marquee flex w-max gap-3 hover:[animation-play-state:paused]">
              {[...hotTags, ...hotTags].map((tag, index) => (
                <Link
                  key={`${tag}-${index}`}
                  to={`/topics?q=${encodeURIComponent(tag)}`}
                  className="whitespace-nowrap rounded-full border border-background-100/20 px-3 py-1 text-xs text-background-100/90 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="sr-only">{siteMeta.slogan}</p>
    </section>
  );
}